import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { EditorToolbar } from './EditorToolbar';
import { newId } from '../../utils/notes';
import type { BlockKind, NoteBlock, Presence } from '../../types/notes';
interface BlockEditorProps {
  blocks: NoteBlock[];
  onChange: (blocks: NoteBlock[]) => void;
  readOnly?: boolean;
  /** Collaborators currently in the note, positioned by block. */
  presence?: Presence[];
}
const TEXT_KINDS: BlockKind[] = ['heading', 'paragraph', 'bulleted', 'numbered', 'checklist', 'quote'];
function blockClasses(block: NoteBlock) {
  const align = block.align === 'center' ? 'text-center' : block.align === 'right' ? 'text-right' : 'text-left';
  switch (block.kind) {
    case 'heading':
      return `${align} font-semibold text-ink ${block.level === 1 ? 'text-2xl leading-8' : block.level === 3 ? 'text-base leading-7' : 'text-lg leading-7'}`;
    case 'quote':
      return `${align} border-l-2 border-brand-700 pl-4 text-[15px] italic leading-7 text-ink-muted`;
    default:
      return `${align} text-[15px] leading-7 text-ink`;
  }
}

/**
 * A real editable surface: one contentEditable node per block, with inline
 * formatting applied to the live selection and block kind changed in place.
 */
export function BlockEditor({
  blocks,
  onChange,
  readOnly,
  presence = []
}: BlockEditorProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const focusNext = useRef<string | null>(null);
  const active = blocks.find(block => block.id === activeId) ?? null;
  useEffect(() => {
    if (!focusNext.current) return;
    const node = refs.current[focusNext.current];
    if (node) {
      node.focus();
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    focusNext.current = null;
  }, [blocks]);
  const patch = useCallback((id: string, next: Partial<NoteBlock>) => {
    onChange(blocks.map(block => block.id === id ? {
      ...block,
      ...next
    } : block));
  }, [blocks, onChange]);
  function commitHtml(id: string) {
    const node = refs.current[id];
    if (!node) return;
    const html = node.innerHTML;
    const block = blocks.find(item => item.id === id);
    if (block && block.html !== html) patch(id, {
      html
    });
  }
  function inline(command: 'bold' | 'italic' | 'underline' | 'strikeThrough' | 'createLink') {
    if (!activeId) return;
    if (command === 'createLink') {
      const href = window.prompt('Link address');
      if (!href) return;
      document.execCommand('createLink', false, href);
    } else {
      document.execCommand(command);
    }
    commitHtml(activeId);
  }
  function setKind(kind: BlockKind, level?: 1 | 2 | 3) {
    if (!activeId) return;
    patch(activeId, {
      kind,
      level: kind === 'heading' ? level ?? 2 : undefined,
      checked: kind === 'checklist' ? false : undefined
    });
  }
  function setAlign(align: 'left' | 'center' | 'right') {
    if (!activeId) return;
    patch(activeId, {
      align
    });
  }
  function insertAfter(kind: 'separator' | 'table') {
    const index = activeId ? blocks.findIndex(block => block.id === activeId) : blocks.length - 1;
    const inserted: NoteBlock = kind === 'separator' ? {
      id: newId('b'),
      kind: 'separator',
      html: ''
    } : {
      id: newId('b'),
      kind: 'table',
      html: '',
      rows: [['Column', 'Column'], ['', '']]
    };
    const trailing: NoteBlock = {
      id: newId('b'),
      kind: 'paragraph',
      html: ''
    };
    const next = [...blocks];
    next.splice(index + 1, 0, inserted, trailing);
    focusNext.current = trailing.id;
    onChange(next);
  }
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>, block: NoteBlock, index: number) {
    if ((event.metaKey || event.ctrlKey) && ['b', 'i', 'u'].includes(event.key.toLowerCase())) {
      event.preventDefault();
      inline(event.key.toLowerCase() === 'b' ? 'bold' : event.key.toLowerCase() === 'i' ? 'italic' : 'underline');
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      commitHtml(block.id);
      // Lists and checklists continue themselves; everything else drops to body text.
      const carry: BlockKind = block.kind === 'bulleted' || block.kind === 'numbered' || block.kind === 'checklist' ? block.kind : 'paragraph';
      const created: NoteBlock = {
        id: newId('b'),
        kind: carry,
        html: '',
        checked: carry === 'checklist' ? false : undefined
      };
      const next = [...blocks];
      next.splice(index + 1, 0, created);
      focusNext.current = created.id;
      onChange(next);
      return;
    }
    const node = refs.current[block.id];
    if (event.key === 'Backspace' && node && node.innerHTML === '' && blocks.length > 1) {
      event.preventDefault();
      if (block.kind !== 'paragraph') {
        patch(block.id, {
          kind: 'paragraph',
          level: undefined,
          checked: undefined
        });
        return;
      }
      const previous = blocks[index - 1];
      const next = blocks.filter(item => item.id !== block.id);
      if (previous) focusNext.current = previous.id;
      onChange(next);
    }
  }
  function updateCell(blockId: string, row: number, column: number, value: string) {
    const block = blocks.find(item => item.id === blockId);
    if (!block?.rows) return;
    const rows = block.rows.map((cells, rowIndex) => rowIndex === row ? cells.map((cell, cellIndex) => cellIndex === column ? value : cell) : cells);
    patch(blockId, {
      rows
    });
  }
  return <div data-id="element-3407" data-mp="132a8un12d1xw">
      {!readOnly && <div className="sticky top-16 z-10" data-id="element-3408" data-mp="17zabenypfmk9">
          <EditorToolbar activeKind={active?.kind ?? null} activeLevel={active?.level} onInline={inline} onKind={setKind} onAlign={setAlign} onInsert={insertAfter} disabled={!activeId} data-id="element-3409" data-mp="18028k37y9scn" />
        </div>}

      <div className="mx-auto max-w-[68ch] px-5 py-6 sm:px-8" data-id="element-3410" data-mp="2kcpah5ztmp54">
        {blocks.map((block, index) => {
        if (block.kind === 'separator') {
          return <div key={block.id} className="group relative my-5 flex items-center" data-id="element-3411" data-mp="2u8a7epq18z80">
                <hr className="w-full border-t border-line-strong" data-id="element-3412" data-mp="2paqrlldosedy" />
                {!readOnly && <button type="button" aria-label="Remove separator" onClick={() => onChange(blocks.filter(item => item.id !== block.id))} className="absolute right-0 rounded-md p-1 text-ink-subtle opacity-0 transition-opacity duration-150 ease-out hover:text-danger-700 group-hover:opacity-100" data-id="element-3413" data-mp="331vls7yacegw">
                    <Trash2Icon className="h-3.5 w-3.5" data-id="element-3414" data-mp="3rm8qxuroljk8" />
                  </button>}
              </div>;
        }
        if (block.kind === 'table') {
          const rows = block.rows ?? [];
          return <div key={block.id} className="my-5" data-id="element-3415" data-mp="3hqf51ks2odps">
                <div className="overflow-x-auto rounded-lg border border-line" data-id="element-3416" data-mp="3mmmbrfsalt8s">
                  <table className="w-full border-collapse text-left" data-id="element-3417" data-mp="38vhak99a0swc">
                    <tbody data-id="element-3418" data-mp="38u67kbnnnp6a">
                      {rows.map((cells, rowIndex) => <tr key={rowIndex} className={rowIndex === 0 ? 'bg-canvas' : ''} data-id="element-3419" data-mp="3dotyeh64svo1" data-mpi={rowIndex}>
                          {cells.map((cell, cellIndex) => <td key={cellIndex} className="border-b border-r border-line p-0 last:border-r-0" data-id="element-3420" data-mp="33w4qfw9shzxg" data-mpi={cellIndex}>
                              <input value={cell} readOnly={readOnly} onChange={event => updateCell(block.id, rowIndex, cellIndex, event.target.value)} aria-label={`Row ${rowIndex + 1} column ${cellIndex + 1}`} className={`w-full bg-transparent px-3 py-2 text-[14px] text-ink focus:bg-brand-50 focus:outline-none ${rowIndex === 0 ? 'font-semibold' : ''}`} data-id="element-3421" data-mp="2tz00rmgqjmbw" />
                            </td>)}
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                {!readOnly && <div className="mt-2 flex flex-wrap gap-2" data-id="element-3422" data-mp="27qcoyczsbmna">
                    <button type="button" onClick={() => patch(block.id, {
                rows: [...rows, rows[0].map(() => '')]
              })} className="inline-flex items-center gap-1 text-[12px] font-medium text-ink-muted hover:text-ink" data-id="element-3423" data-mp="23psrt9f7oj15">
                      <PlusIcon className="h-3 w-3" data-id="element-3424" data-mp="s9rj66ozimcu" /> Row
                    </button>
                    <button type="button" onClick={() => patch(block.id, {
                rows: rows.map(cells => [...cells, ''])
              })} className="inline-flex items-center gap-1 text-[12px] font-medium text-ink-muted hover:text-ink" data-id="element-3425" data-mp="x6i0sygftq0d">
                      <PlusIcon className="h-3 w-3" data-id="element-3426" data-mp="9h6uz2k8d5fd" /> Column
                    </button>
                    <button type="button" onClick={() => onChange(blocks.filter(item => item.id !== block.id))} className="inline-flex items-center gap-1 text-[12px] font-medium text-ink-muted hover:text-danger-700" data-id="element-3427" data-mp="e9qj2tgjniyf">
                      <Trash2Icon className="h-3 w-3" data-id="element-3428" data-mp="36tdbh0318wm1" /> Remove table
                    </button>
                  </div>}
              </div>;
        }
        const marker = block.kind === 'bulleted' ? '•' : block.kind === 'numbered' ? `${blocks.slice(0, index + 1).filter((item, itemIndex) => item.kind === 'numbered' && itemIndex <= index).length}.` : null;
        const here = presence.filter(person => person.blockId === block.id);
        return <div key={block.id} className={`flex gap-2.5 ${block.kind === 'heading' ? 'mt-6 first:mt-0' : 'mt-2 first:mt-0'}`} data-id="element-3429" data-mp="32sl123ifclo6" data-mpi={index}>
              {block.kind === 'checklist' && <input type="checkbox" checked={Boolean(block.checked)} disabled={readOnly} onChange={event => patch(block.id, {
            checked: event.target.checked
          })} aria-label="Toggle item" className="mt-1.5 h-4 w-4 shrink-0 rounded accent-brand-700" data-id="element-3430" data-mp="2abqt8m8jqp42" />}
              {marker && <span aria-hidden="true" className="cz-num mt-0.5 w-4 shrink-0 text-[15px] leading-7 text-ink-subtle" data-id="element-3431" data-mp="dgbk9o8kdaqh">
                  {marker}
                </span>}
              {here.length > 0 && <span className="mt-1.5 flex shrink-0 flex-col gap-0.5" data-id="element-3432" data-mp="3vv48njfu6dth">
                  {here.map(person => <span key={person.handle} style={{
              backgroundColor: person.colour
            }} className="rounded px-1 text-[10px] font-semibold leading-4 text-white" data-id="element-3433" data-mp="3qx2jqhvqbka3">
                      {person.displayName.split(' ')[0]}
                      {person.typing ? '…' : ''}
                    </span>)}
                </span>}
              <div ref={node => {
            refs.current[block.id] = node;
          }} contentEditable={!readOnly} suppressContentEditableWarning role="textbox" aria-multiline="false" aria-label={`${block.kind} block`} data-placeholder={index === 0 ? 'Start writing…' : ''} onFocus={() => setActiveId(block.id)} onBlur={() => commitHtml(block.id)} onKeyUp={() => setActiveId(block.id)} onMouseUp={() => setActiveId(block.id)} onKeyDown={event => onKeyDown(event, block, index)} dangerouslySetInnerHTML={{
            __html: block.html
          }} className={`cz-editable min-w-0 flex-1 rounded-sm outline-none ${block.checked ? 'text-ink-subtle line-through' : ''} ${blockClasses(block)}`} data-id="element-3434" data-mp="3cavl5pz349hy" />
            </div>;
      })}

        {!readOnly && <button type="button" onClick={() => {
        const created: NoteBlock = {
          id: newId('b'),
          kind: 'paragraph',
          html: ''
        };
        focusNext.current = created.id;
        onChange([...blocks, created]);
      }} className="mt-6 w-full rounded-lg border border-dashed border-line-strong py-3 text-[13px] font-medium text-ink-subtle transition-colors duration-150 ease-out hover:border-ink-subtle hover:text-ink" data-id="element-3435" data-mp="2c1e2kue0t0qg">
            Add a paragraph
          </button>}
      </div>
    </div>;
}